/**
 * PropertiesPanel - Edit properties of selected map element
 */
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Inventory as InventoryIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MapElement, ELEMENT_TYPES, ElementType, ElementStatus, ProductStock } from './types';

interface PropertiesPanelProps {
  element: MapElement | null;
  stocks?: ProductStock[];
  onUpdate: (updates: Partial<MapElement>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  /** When set, show “print label” for this saved location (numeric DB id). */
  onPrintLocationLabel?: () => void;
  printingLocationLabel?: boolean;
}

const COLOR_PRESETS = [
  '#e3f2fd', // Blue
  '#e8f5e9', // Green
  '#fff3e0', // Orange
  '#f3e5f5', // Purple
  '#fce4ec', // Pink
  '#e0f7fa', // Cyan
  '#fffde7', // Yellow
  '#efebe9', // Brown
];

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  element,
  stocks = [],
  onUpdate,
  onDelete,
  onDuplicate,
  onPrintLocationLabel,
  printingLocationLabel = false,
}) => {
  const { t } = useTranslation();
  
  if (!element) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('warehouseMap.properties')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('warehouseMap.selectElement')}
        </Typography>
      </Paper>
    );
  }
  
  const typeConfig = ELEMENT_TYPES.find(t => t.type === element.type);
  
  const STATUS_OPTIONS: { value: ElementStatus; label: string; color: string }[] = [
    { value: 'ACTIVE', label: t('warehouseMap.statuses.ACTIVE'), color: '#4caf50' },
    { value: 'INACTIVE', label: t('warehouseMap.statuses.INACTIVE'), color: '#9e9e9e' },
    { value: 'BLOCKED', label: t('warehouseMap.statuses.BLOCKED'), color: '#f44336' },
    { value: 'MAINTENANCE', label: t('warehouseMap.statuses.MAINTENANCE'), color: '#ff9800' },
    { value: 'FULL', label: t('warehouseMap.statuses.FULL'), color: '#e91e63' },
  ];
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontSize={20}>{typeConfig?.icon}</Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            {element.code || element.name}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('warehouseMap.duplicate')}>
            <IconButton size="small" onClick={onDuplicate}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('warehouseMap.delete')}>
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      {/* Basic info */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label={t('warehouseMap.name')}
          value={element.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          size="small"
          fullWidth
        />
        
        <TextField
          label={t('warehouseMap.code')}
          value={element.code || ''}
          onChange={(e) => onUpdate({ code: e.target.value })}
          size="small"
          fullWidth
          placeholder={t('warehouseMap.codePlaceholder')}
        />
        
        <FormControl size="small" fullWidth>
          <InputLabel>{t('warehouseMap.type')}</InputLabel>
          <Select
            value={element.type}
            label={t('warehouseMap.type')}
            onChange={(e) => onUpdate({ type: e.target.value as ElementType })}
          >
            {ELEMENT_TYPES.map((type) => (
              <MenuItem key={type.type} value={type.type}>
                {type.icon} {t(`warehouseMap.elementTypes.${type.type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" fullWidth>
          <InputLabel>{t('warehouseMap.status')}</InputLabel>
          <Select
            value={element.status || 'ACTIVE'}
            label={t('warehouseMap.status')}
            onChange={(e) => onUpdate({ status: e.target.value as ElementStatus })}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: status.color,
                    }}
                  />
                  {status.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {onPrintLocationLabel && (
        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={printingLocationLabel ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={onPrintLocationLabel}
          disabled={printingLocationLabel}
          sx={{ mb: 2 }}
        >
          {t('warehouseMap.printLocationLabel')}
        </Button>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      {/* Position & Dimensions */}
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        {t('warehouseMap.positionAndDimensions')}
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
        <TextField
          label="X"
          type="number"
          value={Math.round(element.position.x)}
          onChange={(e) => onUpdate({
            position: { ...element.position, x: Number(e.target.value) }
          })}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
        />
        <TextField
          label="Y"
          type="number"
          value={Math.round(element.position.y)}
          onChange={(e) => onUpdate({
            position: { ...element.position, y: Number(e.target.value) }
          })}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
        />
        <TextField
          label={t('common.width')}
          type="number"
          value={Math.round(element.dimensions.width)}
          onChange={(e) => onUpdate({
            dimensions: { ...element.dimensions, width: Number(e.target.value) }
          })}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
        />
        <TextField
          label={t('common.height')}
          type="number"
          value={Math.round(element.dimensions.height)}
          onChange={(e) => onUpdate({
            dimensions: { ...element.dimensions, height: Number(e.target.value) }
          })}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
        />
      </Box>
      
      {/* Rotation */}
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        {t('warehouseMap.rotation')}: {Math.round(element.rotation)}°
      </Typography>
      <Slider
        value={element.rotation}
        onChange={(_, value) => onUpdate({ rotation: value as number })}
        min={0}
        max={360}
        step={1}
        size="small"
        sx={{ mb: 2 }}
      />
      
      <Divider sx={{ my: 2 }} />
      
      {/* Color */}
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        {t('warehouseMap.color')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
        {COLOR_PRESETS.map((color) => (
          <Tooltip key={color} title={color}>
            <IconButton
              size="small"
              onClick={() => onUpdate({ color })}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: color,
                border: element.color === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                '&:hover': {
                  backgroundColor: color,
                  opacity: 0.8,
                },
              }}
            />
          </Tooltip>
        ))}
        <TextField
          type="color"
          value={element.color}
          onChange={(e) => onUpdate({ color: e.target.value })}
          size="small"
          sx={{
            width: 28,
            height: 28,
            p: 0,
            '& input': { p: 0, width: 28, height: 28, cursor: 'pointer' },
            '& fieldset': { border: 'none' },
          }}
        />
      </Box>
      
      {/* Capacity */}
      <TextField
        label={t('warehouseMap.maxItems')}
        type="number"
        value={element.maxItems || ''}
        onChange={(e) => onUpdate({
          maxItems: e.target.value ? Number(e.target.value) : null
        })}
        size="small"
        fullWidth
        placeholder={t('warehouseMap.noLimit')}
        sx={{ mb: 2 }}
      />
      
      {/* Stock info */}
      {stocks.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            <InventoryIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
            {t('warehouseMap.content')} ({stocks.length})
          </Typography>
          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
            {stocks.map((stock) => (
              <ListItem key={stock.id} sx={{ px: 0 }}>
                <ListItemText
                  primary={stock.productName}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={`${stock.quantity} ${t('warehouseMap.szt')}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {stock.reservedQuantity > 0 && (
                        <Chip
                          label={`${stock.reservedQuantity} ${t('warehouseMap.reserved')}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Paper>
  );
};

export default PropertiesPanel;

